import React, { useState, ReactNode } from "react";
import ModalReserveMobile from "./ModalReserveMobile";
import MobileCalendar from "components/MobileCalendar/MobileCalendar";
import converSelectedDateToString from "utils/converSelectedDateToString";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { NumericFormat } from "react-number-format";
import { useBooking } from "context/BookingContext";
import { useClient } from "ClientContext";
import { useFetchQuote } from "hooks/useFetchQuote";
import { format } from "date-fns";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import PriceBreakdown from "./PirceBreakdown";
import getSymbolFromCurrency from "currency-symbol-map";
import MobileCalendarQuote from "components/MobileCalendar/MobileCalendarQuote";
import { useTranslation } from "react-i18next";
import MobileCalendarWMinDays from "components/MobileCalendar/MobileCalendarWMinDays";

interface MobileBookingFooterProps { }

const MobileBookingFooter: React.FC<MobileBookingFooterProps> = () => {
  const { t } = useTranslation();
  const [showCouponCode, setShowCouponCode] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const { internal_ID } = useClient();
  const { bookingState, setBookingState } = useBooking();
  const { listingId, startingPrice,roomId } = bookingState;
  const {
    quote,
    isQuote,
    loading,
    quoteError,
    handleFetchQuote,
    setError,
    setIsQuote,
    default_currency,
    couponErrorState,
    setCouponErrorState,
    checkin,
    checkout,
    guestsNum,
  } = useFetchQuote();

  const searchParams = bookingState.searchParams || {};
  const startDate = searchParams.checkIn;
  const endDate = searchParams.checkOut;

  const currencySign = getSymbolFromCurrency(default_currency);

  const getPath = () => {
    if (!quote || !checkin || !checkout) return "";
    return `/checkout/${listingId}/${default_currency}/${checkin}/${checkout}/${guestsNum}/${internal_ID}/${couponCode}`;
  };

  const applyCoupon = () => {
    if (!couponCode.trim()) {
      setCouponError(t("Please enter a coupon code."));
      return;
    }
    setCouponError("");
  };

  const searchWithoutCoupon = () => {
    handleFetchQuote({ listingId,roomId:roomId });
  };

  // --- helper to render each calendar trigger ---
  const commonCalProps = {
    arrival: format(new Date(), "yyyy-MM-dd"),
    departure: null as string | null,
    setIsQuote,
  };

  // --- helper to render each calendar trigger ---
  const renderCalendar = (opts: {
    extraProps?: Partial<typeof commonCalProps & { handleFetchQuote: any }>;
    childrenRenderer: (api: { openModal: () => void }) => ReactNode;
  }): ReactNode => {
    const { extraProps = {}, childrenRenderer } = opts;

    // if handleFetchQuote is present, render the "quote" variant
    // inside your render function / component body:

    if (internal_ID === "placeholder") {
      return (
        <MobileCalendarWMinDays
          {...commonCalProps}
          {...extraProps}
          renderChildren={({ openModal }) => childrenRenderer({ openModal })}
        />
      );
    }

    if (internal_ID === "60ec32ed-8a2c-4a9b-aae4-799793a2e237") {
      return (
        <MobileCalendarQuote
          {...commonCalProps}
          {...extraProps}
          renderChildren={({ openModal }) => childrenRenderer({ openModal })}
        />
      );
    }

    // otherwise render the normal calendar
    return (
      <MobileCalendar
        {...commonCalProps}
        {...extraProps}
        renderChildren={({ openModal }) => childrenRenderer({ openModal })}
      />
    );
  };
  return (
    <div className="block lg:hidden fixed bottom-0 inset-x-0 py-2 sm:py-3 bg-secondary-400 dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-600 z-40">
      <div className="container flex flex-col space-y-2 relative">
        {isQuote && (
          <button
            onClick={() => {
              setIsQuote(false);
              setError(null);
            }}
            className="absolute top-2 right-2 p-2 rounded-full bg-gray-200 dark:bg-neutral-600 text-gray-800 dark:text-white"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}

        {quoteError && quoteError !== "The minimum stay for this rental is 30 days" && (
          (<div className="bg-black text-white px-3 py-2 rounded mb-2">
            <span className="text-sm">{quoteError}</span>
          </div>)
        )}

        <div className="flex items-center justify-between w-full">
          <div className="flex flex-col">
            <span className="block text-xl font-semibold">
              <NumericFormat
                value={isQuote ? quote?.stayTotal : startingPrice}
                displayType={"text"}
                thousandSeparator
                prefix={`${default_currency} ${currencySign}`}
                decimalScale={0}
                className="mr-1 text-neutral-50"
              />
              <span className="ml-1 text-sm font-normal text-neutral-50 dark:text-neutral-400">
                {quote?.stayTotal ? "Total" : t("Starting From")}
              </span>
            </span>
            <div className="flex items-center space-x-2">
              {renderCalendar({
                childrenRenderer: ({ openModal }) => (
                  <span
                    onClick={() => {
                      openModal();
                      setError(null);
                    }}
                    className="block text-sm underline font-medium text-neutral-50"
                  >
                    {converSelectedDateToString([startDate, endDate])} –{" "}
                    {guestsNum} {t("guests")}
                  </span>
                ),
              })}

              {renderCalendar({
                extraProps: { handleFetchQuote },
                childrenRenderer: ({ openModal }) => (
                  <button
                    onClick={() => {
                      if (isQuote) {
                        openModal();
                        setIsQuote(false);
                        setError(null);
                      } else {
                        openModal();
                      }
                    }}
                    className="text-xs text-black-500 underline bg-white px-2 py-1 rounded-md"
                  >
                    {startDate || endDate
                      ? t("Change")
                      : t("Pick Your Dates")}
                  </button>
                ),
              })}
            </div>
          </div>

          <ModalReserveMobile
            setIsQuote={setIsQuote}
            isFooterQuote={isQuote}
            renderChildren={({ openModal }) =>
              loading ? (
                <ButtonSecondary
                  loading={loading}
                  sizeClass="px-5 sm:px-7 py-3 !rounded-2xl"
                  disabled
                >
                  {t("Searching...")}
                </ButtonSecondary>
              ) : (
                !isQuote ? (
                  quoteError === "The minimum stay for this rental is 30 days" ? (
                    <ButtonSecondary onClick={
                      quoteError !== "The minimum stay for this rental is 30 days"
                        ? searchWithoutCoupon
                        : () => window.open(`https://www.thekahalamansion.com/bookinginquiry?checkin=${checkin}&checkout=${checkout}`, "_blank", "noopener,noreferrer")
                    }>
                      Booking Inquiry
                    </ButtonSecondary>
                  ) : (
                    <ButtonSecondary onClick={searchWithoutCoupon}>
                      {t("Search")}
                    </ButtonSecondary>
                  )
                ) : null
              )
            }
          />
        </div>

        {isQuote && (
          <>
            <PriceBreakdown
              quote={quote!}
              default_currency={default_currency}
              showCouponCode={showCouponCode}
              setShowCouponCode={setShowCouponCode}
              couponCode={couponCode}
              setCouponCode={setCouponCode}
              couponError={couponError}
              setCouponError={setCouponError}
              applyCoupon={applyCoupon}
              couponErrorState={couponErrorState}
              setCouponErrorState={setCouponErrorState}
              handleFetchQuote={handleFetchQuote}
              listingId={listingId}
            />
            <ButtonSecondary
              sizeClass="px-5 sm:px-7 py-3 !rounded-2xl w-full"
              className="flex-1 h-12 min-h-12"
              href={getPath()}
            >
              {t("Book Now")}
            </ButtonSecondary>
          </>
        )}
      </div>
    </div>
  );
};

export default MobileBookingFooter;